import { supabase } from '@/integrations/supabase/client';
import { AgentWorkflow, WorkflowNode, WorkflowConnection, WorkflowExecution } from '@/types/agentic';
import { toast } from 'sonner';

// Helper function to convert JSON fields to their expected types
const parseJsonFields = <T extends Record<string, any>>(
  data: Record<string, any>, 
  jsonFields: string[]
): T => {
  const result = { ...data };
  
  for (const field of jsonFields) {
    if (result[field]) {
      result[field] = typeof result[field] === 'string' ? 
        JSON.parse(result[field]) : result[field];
    }
  }
  
  return result as T;
};

export const fetchWorkflows = async (): Promise<AgentWorkflow[]> => {
  const { data, error } = await supabase
    .from('agent_workflows')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching workflows:', error);
    toast.error('Failed to load agent workflows');
    throw error;
  }

  return data.map(item => 
    parseJsonFields<AgentWorkflow>(item, ['canvas_data', 'trigger_config'])
  );
};

export const fetchWorkflowById = async (id: string): Promise<AgentWorkflow | null> => {
  const { data, error } = await supabase
    .from('agent_workflows')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Error fetching workflow with ID ${id}:`, error);
    toast.error('Failed to load workflow details');
    throw error;
  }

  if (!data) return null;
  
  return parseJsonFields<AgentWorkflow>(data, ['canvas_data', 'trigger_config']);
};

export const createWorkflow = async (
  workflow: Omit<AgentWorkflow, 'id' | 'created_at' | 'updated_at'> & { user_id?: string }
): Promise<AgentWorkflow> => {
  const { data, error } = await supabase
    .from('agent_workflows')
    .insert({
      name: workflow.name,
      description: workflow.description,
      created_by: workflow.user_id || workflow.created_by,
      canvas_data: workflow.canvas_data,
      is_active: workflow.is_active,
      trigger_type: workflow.trigger_type,
      trigger_config: workflow.trigger_config
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating workflow:', error);
    toast.error('Failed to create agent workflow');
    throw error;
  }

  toast.success('Agent workflow created successfully');
  return parseJsonFields<AgentWorkflow>(data, ['canvas_data', 'trigger_config']);
};

export const updateWorkflow = async (id: string, workflow: Partial<AgentWorkflow>): Promise<AgentWorkflow> => {
  const { data, error } = await supabase
    .from('agent_workflows')
    .update(workflow)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating workflow with ID ${id}:`, error);
    toast.error('Failed to update agent workflow');
    throw error;
  }

  toast.success('Agent workflow updated successfully');
  return parseJsonFields<AgentWorkflow>(data, ['canvas_data', 'trigger_config']);
};

export const deleteWorkflow = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('agent_workflows')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting workflow with ID ${id}:`, error);
    toast.error('Failed to delete agent workflow');
    throw error;
  }

  toast.success('Agent workflow deleted successfully');
};

export const fetchWorkflowNodes = async (workflowId: string): Promise<WorkflowNode[]> => {
  const { data, error } = await supabase
    .from('workflow_nodes')
    .select(`
      *,
      capability:agent_capabilities(*)
    `)
    .eq('workflow_id', workflowId);

  if (error) {
    console.error(`Error fetching nodes for workflow ${workflowId}:`, error);
    toast.error('Failed to load workflow nodes');
    throw error;
  }

  return data.map(node => {
    const parsedNode = parseJsonFields<WorkflowNode>(node, ['node_config']);
    if (node.capability) {
      parsedNode.capability = parseJsonFields(node.capability, ['config']);
    }
    return parsedNode;
  });
};

export const fetchWorkflowConnections = async (workflowId: string): Promise<WorkflowConnection[]> => {
  const { data, error } = await supabase
    .from('workflow_connections')
    .select('*')
    .eq('workflow_id', workflowId);

  if (error) {
    console.error(`Error fetching connections for workflow ${workflowId}:`, error);
    toast.error('Failed to load workflow connections');
    throw error;
  }

  return data.map(connection => 
    parseJsonFields<WorkflowConnection>(connection, ['condition_config'])
  );
};

export const saveWorkflowNodes = async (
  workflowId: string,
  nodes: Array<Omit<WorkflowNode, 'id' | 'workflow_id' | 'created_at' | 'updated_at'> & { id?: string }>
): Promise<WorkflowNode[]> => {
  const { error: deleteError } = await supabase
    .from('workflow_nodes')
    .delete()
    .eq('workflow_id', workflowId);

  if (deleteError) {
    console.error(`Error deleting existing nodes for workflow ${workflowId}:`, deleteError);
    toast.error('Failed to update workflow nodes');
    throw deleteError;
  }

  const nodesToInsert = nodes.map(node => ({
    workflow_id: workflowId,
    capability_id: node.capability_id,
    node_config: node.node_config,
    position_x: node.position_x,
    position_y: node.position_y
  }));

  const { data, error } = await supabase
    .from('workflow_nodes')
    .insert(nodesToInsert)
    .select();

  if (error) {
    console.error(`Error creating nodes for workflow ${workflowId}:`, error);
    toast.error('Failed to save workflow nodes');
    throw error;
  }

  return data.map(node => 
    parseJsonFields<WorkflowNode>(node, ['node_config'])
  );
};

export const saveWorkflowConnections = async (
  workflowId: string,
  connections: Array<Omit<WorkflowConnection, 'id' | 'workflow_id' | 'created_at'> & { id?: string }>
): Promise<WorkflowConnection[]> => {
  const { error: deleteError } = await supabase
    .from('workflow_connections')
    .delete()
    .eq('workflow_id', workflowId);

  if (deleteError) {
    console.error(`Error deleting existing connections for workflow ${workflowId}:`, deleteError);
    toast.error('Failed to update workflow connections');
    throw deleteError;
  }

  const connectionsToInsert = connections.map(connection => ({
    workflow_id: workflowId,
    source_node_id: connection.source_node_id,
    target_node_id: connection.target_node_id,
    condition_config: connection.condition_config
  }));

  const { data, error } = await supabase
    .from('workflow_connections')
    .insert(connectionsToInsert)
    .select();

  if (error) {
    console.error(`Error creating connections for workflow ${workflowId}:`, error);
    toast.error('Failed to save workflow connections');
    throw error;
  }

  return data.map(connection => 
    parseJsonFields<WorkflowConnection>(connection, ['condition_config'])
  );
};

export const fetchWorkflowExecutions = async (limit: number = 50): Promise<WorkflowExecution[]> => {
  const { data, error } = await supabase
    .from('workflow_executions')
    .select(`
      *,
      workflow:agent_workflows(*)
    `)
    .order('started_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching workflow executions:', error);
    toast.error('Failed to load execution history');
    throw error;
  }

  return data.map(execution => {
    const parsedExecution = parseJsonFields<WorkflowExecution>(
      execution, 
      ['result', 'variables']
    );
    
    if (execution.workflow) {
      parsedExecution.workflow = parseJsonFields(
        execution.workflow, 
        ['canvas_data', 'trigger_config']
      );
    }
    
    return parsedExecution;
  });
};

export const fetchWorkflowExecutionById = async (id: string): Promise<WorkflowExecution | null> => {
  const { data, error } = await supabase
    .from('workflow_executions')
    .select(`
      *,
      workflow:agent_workflows(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Error fetching execution with ID ${id}:`, error);
    toast.error('Failed to load execution details');
    throw error;
  }

  if (!data) return null;
  
  const parsedExecution = parseJsonFields<WorkflowExecution>(
    data, 
    ['result', 'variables']
  );
  
  if (data.workflow) {
    parsedExecution.workflow = parseJsonFields(
      data.workflow, 
      ['canvas_data', 'trigger_config']
    );
  }
  
  return parsedExecution;
};

export const executeWorkflow = async (workflowId: string, variables: Record<string, any> = {}): Promise<WorkflowExecution> => {
  const { data: execution, error: executionError } = await supabase
    .from('workflow_executions')
    .insert({
      workflow_id: workflowId,
      triggered_by: (await supabase.auth.getUser()).data.user?.id,
      status: 'pending',
      variables: variables
    })
    .select()
    .single();

  if (executionError) {
    console.error(`Error creating execution for workflow ${workflowId}:`, executionError);
    toast.error('Failed to start workflow execution');
    throw executionError;
  }

  setTimeout(async () => {
    await supabase
      .from('workflow_executions')
      .update({
        status: 'completed',
        result: { message: 'Workflow executed successfully (simulated)' },
        completed_at: new Date().toISOString()
      })
      .eq('id', execution.id);
    
    toast.success('Workflow executed successfully');
  }, 2000);

  toast.info('Workflow execution started');
  return parseJsonFields<WorkflowExecution>(execution, ['result', 'variables']);
};
